/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "react-query";
import { QueryFunctionContext } from "react-query/types/core/types";
import { ApiClient, ApiError, isServiceUnavailable } from "../async/api-client";
import { useModalManager } from "../contexts/modal-manager-context/ModalManagerProvider";
import { ApiErrorModalContent } from "./api-error-modal-content/ApiErrorModalContent";
import { useApiClient } from "../async/UseApiClient";
import { useTracking } from "../../tracking";
import { useEffect, useRef } from "react";
import { reportApiError } from "../error-reporting/ErrorReporter";

export type UseApiQueryFunction<TRequest, TResponse> = (
  client: ApiClient,
  request: TRequest,
  context?: QueryFunctionContext<QueryKey>,
) => TResponse | Promise<TResponse>;
export type UseApiQueryOptions<TResponse> = Omit<UseQueryOptions<TResponse, ApiError>, 'queryKey' | 'queryFn'>;
export type UseApiQueryResult<TResponse> = UseQueryResult<TResponse, ApiError>;

export type Request<TRequest> = TRequest extends undefined ? undefined : TRequest | null;
export type QueryFnRequest<TRequest> = TRequest extends undefined ? undefined : TRequest;

export function useApiQuery<TResponse, TRequest>(
  queryKey: QueryKey,
  queryFn: UseApiQueryFunction<QueryFnRequest<TRequest>, TResponse>,
  request: Request<TRequest>,
  options?: UseApiQueryOptions<TResponse>,
): UseApiQueryResult<TResponse> {
  const modalManager = useModalManager();
  const apiClient = useApiClient();
  const trackingService = useTracking();

  const modalManagerRef = useRef(modalManager);
  const trackingServiceRef = useRef(trackingService);

  useEffect(() => {
    modalManagerRef.current = modalManager;
    trackingServiceRef.current = trackingService;
  }, [modalManager, trackingService]);

  return useQuery(
    queryKey,
    (context) => queryFn(apiClient, request as QueryFnRequest<TRequest>, context),
    {
      enabled: request !== null,
      staleTime: 5000,
      ...options,
      onError: (error: ApiError) => {
        // Log every request error to VeloX, including the ones for which a page has
        // disabled the global modal via skipGlobalError.
        try {
          reportApiError(error);
        } catch (reportingError) {
          console.error('Failed to report API error:', reportingError);
        }

        if (options?.onError) {
          // Isolate the caller's handler so a page-level bug can't block the error modal.
          try {
            options.onError(error);
          } catch (callerError) {
            console.error('useApiQuery onError handler failed:', callerError);
          }
        }
        // An unavailable service (no HTTP response, or a 502/503/504 from the backend) always
        // surfaces the global modal so users understand the backend is unreachable,
        // even when a page opts out of generic error handling via `skipGlobalError`.
        if (!(options as any)?.skipGlobalError || isServiceUnavailable(error)) {
          // Isolate tracking so a telemetry bug can never block the error modal.
          try {
            trackingServiceRef.current.trackFormFormErrorMessageLoad({
              contentId: 'error',
              error: error
            });
          } catch (trackingError) {
            console.error('Failed to track error message load:', trackingError);
          }

          modalManagerRef.current.open({
            title: 'Error',
            content: () => React.createElement(ApiErrorModalContent, { error }),
            actions: [
              {
                title: 'Close',
                type: 'primary',
                key: 'close',
                onClick(close) {
                  close();
                },
              },
            ],
          });
        }
      },
    }
  );
}
